// src/services/guestActions.js — react, delete, and the reply plumbing, for the
// guest page.
//
// 🟥 THE SHAPES ARE THE PHONE'S, NOT NEW ONES. A reaction is a `reactions` map on
//    the message document PLUS a socket event the server already knows how to
//    turn into a push; a delete is `deletedFor` (mine) or `isDeleted` (everyone's).
//    Every one of those field names is read by ChatModal.tsx and by the web's own
//    ChatWindow, so a guest's action lands on the phone as an ordinary action
//    rather than as something nobody parses.
//
// 🟢 AND THIS IS WHY REACTIONS REACH iOS AND ANDROID NOTIFICATIONS FOR FREE.
//    A reaction is NOT a message — it creates no message document — so it cannot
//    ride the ordinary push path. The server has a dedicated branch for exactly
//    this: an event with `systemType` and `targetMessageId` and NO `messageId` is
//    treated as a system event, relayed on the fast path, and its emoji/target
//    are forwarded into the FCM payload so the phone can apply it on receipt.
//    (See server/functions/index.js — "SYSTEM EVENTS HAVE NO messageId".) All
//    this file has to do is send the event in the shape that branch expects.
//    A REPLY needs none of that: it is a real message, so it pushes like one.

import { doc, updateDoc, arrayUnion, serverTimestamp, onSnapshot, addDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getSocket } from './socket';
import { replySnippet } from './replies';

/** The six the sheet offers — the phone's own set, in its own order. */
export const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

/**
 * Toggle my reaction on a message.
 *
 * 🟢 ONE EMOJI PER PERSON, and picking a new one REPLACES the old rather than
 *    stacking — the same rule the phone applies. A swap therefore travels as two
 *    intents, remove-then-add, so the receiving side applies it with the ordinary
 *    single-reaction logic and never has to special-case a "change".
 */
export async function toggleReaction({ chatId, message, myUid, myName, emoji, toUserId, recipients, isGroup }) {
  if (!chatId || !message?.id || !emoji) return null;

  const current = message.reactions || {};
  const mineNow = Object.keys(current).find((e) => (current[e] || []).includes(myUid)) || null;
  const removing = mineNow === emoji;

  const next = {};
  for (const [e, list] of Object.entries(current)) {
    const kept = (list || []).filter((u) => String(u) !== String(myUid));
    if (kept.length) next[e] = kept;
  }
  if (!removing) next[emoji] = [...(next[emoji] || []), myUid];

  const ts = Date.now();
  /* 🟥 THE STAMP MAP IS KEYED BY EMOJI AND THEN BY PERSON, not by person alone.
     The phone writes `reactionsAt: { [emoji]: { [uid]: ms } }` (stores/MessageStore.ts)
     and both clients' rosters read that shape to decide which reaction a member
     holds when the map somehow lists them twice. A flat `reactionsAt.<uid>` was a
     field nothing reads — written, carried forever, and never consulted. */
  /* 🟥 THIS WRITE IS REFUSED FOR A MESSAGE SOMEBODY ELSE SENT — the rules let only the sender
        update a message — and it was not caught: reacting to the other person's message threw,
        and nothing after it (the socket emits) ever ran. That is every reaction a guest makes,
        and the same on the full web client. 🟢 Tried, never relied on: the durable half is the
        `reaction_action` documents below, one per intent, the same shape the phone writes
        (services/chatReactions), which every client replays. */
  try {
    await updateDoc(doc(db, 'chats', chatId, 'messages', message.id), {
      reactions: next,
      [`reactionsAt.${emoji}.${myUid}`]: ts,
    });
  } catch { /* not my message — the intents below carry it */ }

  /* the intents, in the order the phone sends them: drop the old, then add the
     new. A plain toggle-off is a single 'remove'. A swap's two steps get strictly
     increasing stamps so a replay applies them in order. */
  const intents = [];
  if (mineNow && mineNow !== emoji) intents.push({ emoji: mineNow, action: 'remove', ts });
  intents.push({ emoji, action: removing ? 'remove' : 'add', ts: intents.length ? ts + 1 : ts });
  for (const it of intents) {
    try {
      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        type: 'reaction_action',
        targetMessageId: message.id,
        action: it.action,
        emoji: it.emoji,
        senderId: myUid,
        timestamp: serverTimestamp(),
        reactionTs: it.ts,
      });
    } catch { /* offline / the chat ended — the socket copy below still goes */ }
  }

  const snippet = replySnippet({ text: message._plain || message.text, type: message.type });
  /* 🟢 ONE EVENT PER RECIPIENT, which is how the phone sends it too. A guest page
     has exactly one counterpart and passes `toUserId`; the full client passes the
     group's members in `recipients` — the server fans a reaction out per socket,
     so a single event addressed to a group reaches nobody. */
  const to = Array.isArray(recipients) && recipients.length
    ? recipients.map(String).filter((u) => u && u !== String(myUid))
    : (toUserId ? [String(toUserId)] : []);
  for (const it of intents) {
    for (const uid of to) {
      try {
        getSocket()?.emit('new-message', {
          toUserId: uid,
          fromUserId: myUid,
          // 🟢 the notification's own words — the phone shows this verbatim
          message: `Reacted ${it.emoji} to: "${snippet}"`,
          chatId,
          isGroup: isGroup ? 'true' : 'false',
          systemType: 'reaction',
          emoji: it.emoji,
          targetMessageId: message.id,
          action: it.action,
          reactionTs: it.ts,
          senderName: myName || 'Guest',
        });
      } catch { /* the Firestore write above is the durable half */ }
    }
  }

  return next;
}

/**
 * Hide a message for me alone.
 *
 * 🟢 `deletedFor` IS AN arrayUnion, not a rewrite, because both sides may be
 *    deleting at once and last-write-wins would silently un-delete one of them.
 */
export function deleteForMe({ chatId, messageId, myUid }) {
  if (!chatId || !messageId) return Promise.resolve();
  return updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
    deletedFor: arrayUnion(myUid),
  });
}

/**
 * Retract a message for everyone.
 *
 * 🟥 THE CONTENT IS CLEARED, NOT JUST FLAGGED. Leaving the ciphertext in place and
 *    setting a boolean means the message is one modified client away from being
 *    readable — "deleted for everyone" has to actually remove it. The document
 *    stays so both sides can render the "This message was deleted" placeholder in
 *    the right position in the thread.
 */
export function deleteForEveryone({ chatId, messageId, myUid, senderId }) {
  if (!chatId || !messageId) return Promise.resolve();
  if (String(senderId) !== String(myUid)) {
    return Promise.reject(new Error('You can only delete your own messages for everyone.'));
  }
  return updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    text: '',
    perDeviceKeys: {},
    imageUrl: null,
    imagePerDeviceKeys: {},
    videoUrl: null,
    videoPerDeviceKeys: {},
    audioUrl: null,
    audioPerDeviceKeys: {},
    documentUrl: null,
    documentPerDeviceKeys: {},
    documentThumbUrl: null,
    documentThumbPerDeviceKeys: {},
    images: [],
    batchPerDeviceKeys: {},
    locationPerDeviceKeys: {},
    reactions: {},
  });
}

/** Is this message hidden from me? Either side of a delete. */
export const isHidden = (m, myUid) =>
  !!(m && Array.isArray(m.deletedFor) && m.deletedFor.includes(myUid));


/* ══ THE READ WATERMARK ════════════════════════════════════════════════════
   🟥 A TICK MUST NOT DEPEND ON A PER-MESSAGE WRITE REACHING EVERY MESSAGE.
      `seen: true` is written one document at a time by whoever is reading, and
      any of those writes can be missed — the reader was on a build that batches
      differently, the write lost a race, a permission blip swallowed it (the
      helper that writes it catches and discards its own errors). When one is
      missed, that message is stranded on two grey ticks forever, because nothing
      ever revisits it.

      The phone never relied on those flags for this. It compares the message's
      time against `readUpTo[otherUid]` on the CHAT document — one watermark, and
      everything at or before it has been read by definition. That is both more
      robust and cheaper, and it is why a chat opened on the phone turns a whole
      screen of ticks at once.

      So the guest reads BOTH: the per-message flag when it is there, and the
      watermark otherwise. Either is enough; neither alone is reliable. */
export function watchChatMeta(chatId, cb) {
  if (!chatId) { cb(null); return () => {}; }
  return onSnapshot(doc(db, 'chats', chatId), (s) => cb(s.exists() ? s.data() : null),
    (e) => { console.warn('[guest] chat meta listener failed:', e && (e.code || e.message)); cb(null); });
}

/** Whatever shape the watermark was written in, in milliseconds. */
export function watermarkMs(chat, uid, field = 'readUpTo') {
  const v = chat?.[field]?.[uid];
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') { try { return v.toMillis(); } catch { return 0; } }
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
}

/** Milliseconds for a message, across the shapes a timestamp arrives in. */
export function msgMs(m) {
  const t = m?.timestamp;
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t.toMillis === 'function') { try { return t.toMillis(); } catch { return 0; } }
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  const d = Date.parse(t);
  return Number.isNaN(d) ? 0 : d;
}
